import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "../../layout/Layout";
import { useAuth } from "../../../components/context/AuthContext";
import {
  getDofIstekById,
  updateDofIstek,
} from "../../../api/kalite/dofApi";
import { getPersonel } from "../../../api/authApi";
import {
  boolVal,
  cleanDofPayload,
  toDateInputValue,
  ASSIGNMENT_LABELS,
  getMissingWorkflowRoleLabels,
  initials,
  getDofStatus,
  getStatusMeta,
} from "./dofFormUtils";
import { ArrowLeft, Info, ShieldCheck } from "lucide-react";
import Select from "react-select";
import DofWizardSteps, { WIZARD_STEPS } from "./DofWizardSteps";
import DofPersonelSelect from "./DofPersonelSelect";
import "./Dof.css";

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("tr-TR");
};

const EDIT_STEPS = [
  { step: 1, label: "DÖF Bilgileri" },
  { step: 2, label: "Uygunsuzluk ve Atama" },
];

const STATUS_OPTIONS = [
  ["acik", "Açık (sorumluda)"],
  ["onay_bekliyor", "Onay Bekliyor"],
  ["takipte", "Onaylandı / Takipte"],
  ["kapali", "Kapalı"],
];

const EDITABLE_RECORD_FIELDS = [
  "dofIstekTarih",
  "faliyetTuru",
  "tekrarUygunsuzluk",
  "icDenetim",
  "disDenetim",
  "iyilestirme",
  "icProsesSikayet",
  "musteriSikayet",
  "digerAciklama",
  "uygunsuzlukTanimi",
  "dofiTalepEden",
  "dofIstenenBirim",
  "assignedUsers",
];

function DofEdit() {
  const { user } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState(1);
  const [fetching, setFetching] = useState(true);
  const [saving, setSaving] = useState(false);

  // Backend'in döndüğü yetki ve kayıt bilgileri (formdan ayrı tutulur, geri gönderilmez)
  const [access, setAccess] = useState(null);
  const [recordInfo, setRecordInfo] = useState({
    createdBy: null,
    status: "",
    actions: [],
    notifications: [],
    createdAt: null,
  });

  const readOnly = !access?.canEdit;
  // Kalite tam yetki: aşamadan bağımsız tüm alanlar + durum + DÖF No düzenlenir (eski DÖF girişi için)
  const fullAccess = Boolean(access?.fullAccess);
  const steps = fullAccess ? WIZARD_STEPS : EDIT_STEPS;
  const lastStep = steps.length;
  const [manualStatus, setManualStatus] = useState("acik");
  const [sendMail, setSendMail] = useState(true);

  const [personelLoading, setPersonelLoading] = useState(false);
  const [personelList, setPersonelList] = useState([]);

  const [assignedUserForm, setAssignedUserForm] = useState({
    userId: "",
    fullName: "",
    email: "",
    departman: "",
    gorev: "",
    assignmentType: "sorumlu",
  });

  const [formData, setFormData] = useState({
    dofNo: "",
    dofIstekTarih: "",
    faliyetTuru: "",
    tekrarUygunsuzluk: "",
    icDenetim: "",
    disDenetim: "",
    iyilestirme: "",
    icProsesSikayet: "",
    musteriSikayet: "",
    digerAciklama: "",
    uygunsuzlukTanimi: "",
    dofiTalepEden: "",
    kokNeden: "",
    duzetmeFaliyetTanimi: "",
    duzeltmeSorumlu: "",
    duzeltmeTerminSure: "",
    duzelticiFaliyetTanimi: "",
    duzelticiSorumlu: "",
    duzelticiTerminSure: "",
    onleyiciciFaliyetTanimi: "",
    onleyiciciSorumlu: "",
    onleyiciciTerminSure: "",
    takipTarihi: "",
    takibiGerceklestiren: "",
    takipSonucuOneri: "",
    prosedur: "",
    prosedurRevTarihi: "",
    prosedurRevNo: "",
    talimat: "",
    talimatRevTarihi: "",
    talimatRevNo: "",
    isAkisi: "",
    isAkisiRevTarihi: "",
    isAkisiRevNo: "",
    form: "",
    formRevTarihi: "",
    formRevNo: "",
    digerRevAciklama: "",
    digerRevTarihi: "",
    digerRevNo: "",
    yeniDofAcilmali: "",
    dofKapatmaTarihiOnay: "",
    yonetimTemsilcisi: "",
    dofIstenenBirim: "",
    assignedUsers: [],
  });

  useEffect(() => {
    const fetchDofData = async (dofId) => {
      try {
        setFetching(true);

        const response = await getDofIstekById(dofId);

        if (response && response.data && response.data.success) {
          const {
            _access,
            createdBy,
            status,
            actions,
            notifications,
            createdAt,
            ...dofData
          } = response.data.data || {};

          setAccess(_access || null);
          setManualStatus(status || "acik");
          setSendMail(!dofData.gecmisKayit);
          setRecordInfo({
            createdBy: createdBy || null,
            status: status || "",
            actions: Array.isArray(actions) ? actions : [],
            notifications: Array.isArray(notifications) ? notifications : [],
            createdAt: createdAt || null,
          });

          setFormData((prev) => ({
            ...prev,
            ...dofData,
            assignedUsers: Array.isArray(dofData.assignedUsers)
              ? dofData.assignedUsers
              : [],
          }));
        } else {
          alert("DÖF verisi bulunamadı.");
          navigate("/DofList");
        }
      } catch (error) {
        console.error("DÖF Verisi Çekme Hatası:", error);
        alert(
          error.response?.data?.message ||
            "Veri getirilirken bir hata oluştu."
        );
        navigate("/DofList");
      } finally {
        setFetching(false);
      }
    };

    if (id) {
      fetchDofData(id);
    }
  }, [id, navigate]);

  useEffect(() => {
    const fetchPersonel = async () => {
      setPersonelLoading(true);

      try {
        const response = await getPersonel();

        const list =
          response?.data?.data ||
          response?.data?.results ||
          response?.data ||
          [];

        setPersonelList(Array.isArray(list) ? list : []);
      } catch (error) {
        console.error("Personel listesi alınamadı:", error);
        setPersonelList([]);
      } finally {
        setPersonelLoading(false);
      }
    };

    fetchPersonel();
  }, []);

  const getPersonelId = (personel) => {
    return personel?._id || personel?.id || "";
  };

  const getPersonelFullName = (personel) => {
    return (
      personel?.fullName ||
      personel?.nameSurname ||
      personel?.nameSurnameText ||
      `${personel?.name || ""} ${personel?.surname || ""}`.trim() ||
      personel?.username ||
      ""
    );
  };

  const getPersonelDepartman = (personel) => {
    return (
      personel?.departman ||
      personel?.department ||
      personel?.departmentName ||
      personel?.birim ||
      personel?.unit ||
      ""
    );
  };

  const getPersonelGorev = (personel) => {
    return (
      personel?.gorev ||
      personel?.title ||
      personel?.position ||
      personel?.unvan ||
      personel?.roleName ||
      personel?.role?.name ||
      ""
    );
  };

  const getPersonelEmail = (personel) => {
    return String(personel?.email || "")
      .trim()
      .toLowerCase();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    let parsedValue = value;

    if (value === "true") parsedValue = true;
    if (value === "false") parsedValue = false;

    setFormData((prev) => ({
      ...prev,
      [name]: parsedValue,
    }));
  };

  const handlePersonelSelect = (e) => {
    const selectedId = e.target.value;

    if (!selectedId) {
      setAssignedUserForm((prev) => ({
        ...prev,
        userId: "",
        fullName: "",
        email: "",
        departman: "",
        gorev: "",
      }));
      return;
    }

    const selectedPersonel = personelList.find(
      (item) => String(getPersonelId(item)) === String(selectedId)
    );

    if (!selectedPersonel) {
      alert("Seçilen personel bulunamadı.");
      return;
    }

    setAssignedUserForm((prev) => ({
      ...prev,
      userId: getPersonelId(selectedPersonel),
      fullName: getPersonelFullName(selectedPersonel),
      email: getPersonelEmail(selectedPersonel),
      departman: getPersonelDepartman(selectedPersonel),
      gorev: getPersonelGorev(selectedPersonel),
    }));
  };

  const addAssignedUser = () => {
    const userId = String(assignedUserForm.userId || "").trim();
    const fullName = String(assignedUserForm.fullName || "").trim();
    const email = String(assignedUserForm.email || "")
      .trim()
      .toLowerCase();
    const departman = String(assignedUserForm.departman || "").trim();
    const gorev = String(assignedUserForm.gorev || "").trim();
    const assignmentType = assignedUserForm.assignmentType || "sorumlu";

    if (!userId) {
      alert("Lütfen personel seçin.");
      return;
    }

    if (!fullName) {
      alert("Seçilen personelin ad soyad bilgisi bulunamadı.");
      return;
    }

    if (!email) {
      alert("Seçilen personelin e-posta bilgisi bulunamadı.");
      return;
    }

    const alreadyExists = (formData.assignedUsers || []).some((item) => {
      const sameUserId = String(item.userId || "") === String(userId);
      const sameEmail =
        String(item.email || "")
          .trim()
          .toLowerCase() === email;

      return (sameUserId || sameEmail) && item.assignmentType === assignmentType;
    });

    if (alreadyExists) {
      alert("Bu kişi bu rolle zaten eklenmiş.");
      return;
    }

    const newAssignedUser = {
      userId,
      fullName,
      email,
      departman,
      gorev,
      assignmentType,
    };

    setFormData((prev) => ({
      ...prev,
      assignedUsers: [...(prev.assignedUsers || []), newAssignedUser],
    }));

    setAssignedUserForm({
      userId: "",
      fullName: "",
      email: "",
      departman: "",
      gorev: "",
      assignmentType: "sorumlu",
    });
  };

  const removeAssignedUser = (index) => {
    setFormData((prev) => ({
      ...prev,
      assignedUsers: (prev.assignedUsers || []).filter(
        (_, itemIndex) => itemIndex !== index
      ),
    }));
  };

  const nextStep = () => setCurrentStep((prev) => (prev < lastStep ? prev + 1 : prev));
  const prevStep = () => setCurrentStep((prev) => (prev > 1 ? prev - 1 : prev));

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (readOnly) return;

    // Enter tuşu ile form son adımdan önce gönderilmesin.
    if (currentStep < lastStep) {
      nextStep();
      return;
    }

    const missingRoles = getMissingWorkflowRoleLabels(formData.assignedUsers);
    if (missingRoles.length) {
      if (!fullAccess) {
        alert(`İş akışını sürdürebilmek için ${missingRoles.join(", ")} rolüne kişi atayın.`);
        setCurrentStep(2);
        return;
      }
      if (
        manualStatus !== "kapali" &&
        !formData.gecmisKayit &&
        !window.confirm(`${missingRoles.join(", ")} rolüne kimse atanmadı. Bu adımları tam yetkinizle kendiniz yürütebilirsiniz. Devam edilsin mi?`)
      ) {
        setCurrentStep(2);
        return;
      }
    }

    setSaving(true);

    try {
      const cleanPayload = cleanDofPayload(formData);
      const payload = fullAccess
        ? {
            ...cleanPayload,
            id,
            dofNo: String(formData.dofNo || "").trim(),
            status: manualStatus,
            gecmisKayit: formData.gecmisKayit === true,
            sendMail,
          }
        : EDITABLE_RECORD_FIELDS.reduce(
            (result, field) =>
              cleanPayload[field] === undefined
                ? result
                : { ...result, [field]: cleanPayload[field] },
            { id }
          );
      delete payload.kaynakDofId;

      const response = await updateDofIstek(payload);

      if (response && response.data && response.data.success) {
        alert(response.data.message || "DÖF Raporu başarıyla güncellendi.");
        navigate("/DofList");
      } else {
        alert("Güncelleme işlemi sırasında bir sorun oluştu.");
      }
    } catch (error) {
      console.error("DÖF Güncelleme Hatası:", error);
      alert(
        error.response?.data?.message ||
          "Sunucu ile iletişim kurulurken bir hata oluştu."
      );
    } finally {
      setSaving(false);
    }
  };

  if (!user) return null;

  if (fetching) {
    return (
      <Layout>
        <div style={{ padding: 50, textAlign: "center" }}>
          <h4>Veriler yükleniyor...</h4>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="dof-page">
        <section className="dof-hero">
          <div>
            <span className="dof-kicker">
              <ShieldCheck size={15} /> {fullAccess ? "Kalite Tam Yetki · Tüm Alanlar" : "Kalite Yetkilisi · Kayıt ve Atamalar"}
            </span>
            <h1>
              {formData.dofNo || "DÖF"} Düzenle{" "}
              <span
                className={`dof-badge st-${getDofStatus({ ...formData, status: recordInfo.status })}`}
                style={{ verticalAlign: "middle", fontSize: 13 }}
              >
                {getStatusMeta(getDofStatus({ ...formData, status: recordInfo.status })).label}
              </span>
            </h1>
            {fullAccess ? (
              <p>
                Tam yetkinizle DÖF'ün tüm alanlarını (kök neden, faaliyetler, takip ve kapatma dahil) hangi aşamada olursa olsun
                düzenleyebilir, durumunu elle belirleyebilirsiniz. Eski DÖF'leri girmek için de bu ekranı kullanabilirsiniz.
              </p>
            ) : (
              <p>
                Bu ekranda kayıt bilgilerini ve rol atamalarını düzenleyebilirsiniz. Kök neden ve faaliyetleri yalnızca
                Sorumlu; onay kararını Onaycı; takip ve kapatma işlemini Takipçi detay sayfasından yapar.
              </p>
            )}
            <div className="dof-hero-meta">
              {recordInfo.createdBy?.fullName && (
                <span>
                  Açan: {recordInfo.createdBy.fullName}
                  {recordInfo.createdAt ? ` · ${formatDateTime(recordInfo.createdAt)}` : ""}
                </span>
              )}
            </div>
          </div>
          <div className="dof-hero-actions">
            <button type="button" className="dof-btn dof-btn-ghost" onClick={() => navigate(`/DofDetay/${id}`)}>
              <ArrowLeft size={16} /> Detaya Dön
            </button>
          </div>
        </section>

        {readOnly && (
          <div className="dof-callout warn">
            <Info size={17} />
            <div>Bu kaydı düzenleme yetkiniz yok; form salt okunur gösteriliyor.</div>
          </div>
        )}

        <section className="dof-panel dof-wizard">
          <DofWizardSteps currentStep={currentStep} onSelect={setCurrentStep} steps={steps} />
          <div className="dof-wizard-body">
        <form onSubmit={handleSubmit}>
          <fieldset disabled={readOnly} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
          {currentStep === 1 && (
            <div>
              <h5 className="mb-3">DÖF Bilgileri</h5>

              <div className="row mb-3">
                <div className="col-md-6">
                  <label className="form-label">DÖF NO</label>
                  <input
                    type="text"
                    name="dofNo"
                    className="form-control"
                    value={formData.dofNo || ""}
                    disabled={!fullAccess}
                    readOnly={!fullAccess}
                    onChange={handleChange}
                  />
                  <span className="dof-hint">
                    {fullAccess
                      ? "Tam yetki: eski DÖF'ün kağıttaki numarasını girebilirsiniz (benzersiz olmalı)."
                      : "DÖF No sistem tarafından verilir, değiştirilemez."}
                  </span>
                </div>

                <div className="col-md-6">
                  <label className="form-label">DÖF İstek Tarihi</label>
                  <input
                    type="date"
                    name="dofIstekTarih"
                    className="form-control"
                    value={toDateInputValue(formData.dofIstekTarih)}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-12">
                  <label className="form-label">Faaliyet Türü</label>
                  <div className="dof-tabs" style={{ maxWidth: 460 }}>
                    {[
                      ["duzeltici", "Düzeltici Faaliyet"],
                      ["onleyici", "Önleyici Faaliyet"],
                    ].map(([value, label]) => (
                      <button
                        type="button"
                        key={value}
                        className={`dof-tab ${formData.faliyetTuru === value ? "active" : ""}`}
                        style={{ flex: 1, justifyContent: "center" }}
                        onClick={() => setFormData((prev) => ({ ...prev, faliyetTuru: value }))}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label">Tespit Türü</label>
                <div className="d-flex flex-wrap" style={{ gap: 8 }}>
                  {[
                    ["tekrarUygunsuzluk", "Tekrarlayan Uygunsuzluk"],
                    ["icDenetim", "İç Denetim"],
                    ["disDenetim", "Dış Denetim"],
                    ["iyilestirme", "İyileştirme"],
                    ["icProsesSikayet", "İç Proses Şikayeti"],
                    ["musteriSikayet", "Müşteri Şikayeti"],
                  ].map(([key, label]) => {
                    const on = formData[key] === true || formData[key] === "true";
                    return (
                      <button
                        type="button"
                        key={key}
                        className={`dof-toggle ${on ? "on" : ""}`}
                        onClick={() => setFormData((prev) => ({ ...prev, [key]: !on }))}
                      >
                        <span className="dof-toggle-box">{on ? "✓" : ""}</span>
                        {label}
                      </button>
                    );
                  })}
                </div>
                <span className="dof-hint">Birden fazla seçilebilir. Seçilmeyenler “Hayır” kabul edilir.</span>
              </div>

              <div className="mb-3">
                <label className="form-label">Diğer (Açıklama)</label>
                <input
                  type="text"
                  name="digerAciklama"
                  className="form-control"
                  placeholder="Listede olmayan bir tespit kaynağı varsa yazınız"
                  value={formData.digerAciklama || ""}
                  onChange={handleChange}
                />
              </div>

              <div className="text-end">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={nextStep}
                  disabled={saving}
                >
                  İleri
                </button>
              </div>
            </div>
          )}

          {currentStep === 2 && (
            <div>
              <h5 className="mb-3">Uygunsuzluk Tanımı</h5>

              <div className="row mb-3">
                <div className="col-md-12 mb-3">
                  <label className="form-label">Uygunsuzluk Tanımı</label>
                  <textarea
                    name="uygunsuzlukTanimi"
                    className="form-control"
                    rows="3"
                    value={formData.uygunsuzlukTanimi || ""}
                    onChange={handleChange}
                  />
                </div>

                <div className="col-md-12 mb-3">
                  <label className="form-label">
                    DÖF Talep Eden Kişi/Departman
                  </label>
                  <textarea
                    name="dofiTalepEden"
                    className="form-control"
                    rows="3"
                    value={formData.dofiTalepEden || ""}
                    onChange={handleChange}
                  />
                </div>

                <div className="col-md-12 mb-3">
                  <h5 className="mb-3 border-top pt-3">
                    Sorumlu Kişi / Departman Ataması
                  </h5>

                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Personel Seç</label>
                      <Select
                        classNamePrefix="dof-select"
                        isClearable
                        isLoading={personelLoading}
                        isDisabled={personelLoading || saving}
                        placeholder={personelLoading ? "Personel listesi yükleniyor..." : "İsim, e-posta veya departman ile ara..."}
                        noOptionsMessage={() => "Personel bulunamadı"}
                        options={personelList.map((personel) => ({
                          value: String(getPersonelId(personel)),
                          label: getPersonelFullName(personel) || "İsimsiz Personel",
                          email: getPersonelEmail(personel),
                          departman: getPersonelDepartman(personel),
                        }))}
                        value={
                          assignedUserForm.userId
                            ? {
                                value: String(assignedUserForm.userId),
                                label: assignedUserForm.fullName || "Seçili personel",
                                email: assignedUserForm.email,
                                departman: assignedUserForm.departman,
                              }
                            : null
                        }
                        filterOption={(option, input) => {
                          const q = String(input || "").toLocaleLowerCase("tr-TR");
                          if (!q) return true;
                          return [option.data.label, option.data.email, option.data.departman]
                            .join(" ")
                            .toLocaleLowerCase("tr-TR")
                            .includes(q);
                        }}
                        formatOptionLabel={(option) => (
                          <div className="dof-person-option">
                            <strong>{option.label}</strong>
                            <small>{[option.departman, option.email].filter(Boolean).join(" · ")}</small>
                          </div>
                        )}
                        onChange={(option) => handlePersonelSelect({ target: { value: option?.value || "" } })}
                      />
                    </div>

                    <div className="col-md-6 mb-3">
                      <label className="form-label">Atama Tipi</label>
                      <div className="dof-tabs" style={{ width: "100%" }}>
                        {["sorumlu", "onayci", "takipci", "bilgi"].map((type) => (
                          <button
                            type="button"
                            key={type}
                            className={`dof-tab ${assignedUserForm.assignmentType === type ? "active" : ""}`}
                            style={{ flex: 1, justifyContent: "center" }}
                            onClick={() => setAssignedUserForm((prev) => ({ ...prev, assignmentType: type }))}
                          >
                            {ASSIGNMENT_LABELS[type]}
                          </button>
                        ))}
                      </div>
                      <span className="dof-hint">
                        {
                          {
                            sorumlu: "Kök neden ve faaliyetleri girip onaya gönderir.",
                            onayci: "Sorumlunun girdiği faaliyetleri onaylar / reddeder.",
                            takipci: "Onaydan sonra etkinliği doğrulayıp DÖF'ü kapatır.",
                            bilgi: "Sadece bilgilendirilir, işlem yapmaz.",
                          }[assignedUserForm.assignmentType]
                        }
                      </span>
                    </div>

                    <div className="col-md-12 text-end mb-3">
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={addAssignedUser}
                        disabled={saving || personelLoading || !assignedUserForm.userId}
                      >
                        + {ASSIGNMENT_LABELS[assignedUserForm.assignmentType]} Olarak Ekle
                      </button>
                    </div>
                  </div>

                  {(formData.assignedUsers || []).length > 0 ? (
                    <div className="dof-people" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))" }}>
                      {(formData.assignedUsers || []).map((item, index) => (
                        <div
                          className="dof-person"
                          key={`${item.userId}-${item.email}-${item.assignmentType}-${index}`}
                          style={{ padding: 10, border: "1px solid var(--border)", borderRadius: 12 }}
                        >
                          <span className={`dof-avatar lg role-${item.assignmentType}`}>{initials(item.fullName)}</span>
                          <div>
                            <strong>{item.fullName || item.email}</strong>
                            <small>{[item.departman, item.email].filter(Boolean).join(" · ")}</small>
                          </div>
                          <span className={`dof-badge role-${item.assignmentType}`}>
                            {ASSIGNMENT_LABELS[item.assignmentType] || item.assignmentType}
                          </span>
                          <button
                            type="button"
                            className="dof-icon-btn danger"
                            title="Kaldır"
                            onClick={() => removeAssignedUser(index)}
                            disabled={saving}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="dof-callout warn">
                      Henüz kimse atanmadı. En az birer <b>Sorumlu</b>, <b>Onaycı</b> ve <b>Takipçi</b> ekleyin.
                    </div>
                  )}
                </div>
              </div>

              <div className="d-flex justify-content-between">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={prevStep}
                  disabled={saving}
                >
                  Geri
                </button>

                {fullAccess ? (
                  <button type="button" className="btn btn-primary" onClick={nextStep} disabled={saving}>
                    İleri
                  </button>
                ) : (
                  <button
                    type="submit"
                    className="btn btn-success"
                    disabled={saving}
                  >
                    {saving ? "Kaydediliyor..." : "Değişiklikleri Kaydet"}
                  </button>
                )}
              </div>
            </div>
          )}

          {currentStep === 3 && (
            <div>
              <h5 className="mb-3">Kök Neden</h5>

              <div className="row mb-3">
                <div className="col-md-12">
                  <label className="form-label">Kök Neden</label>
                  <textarea
                    name="kokNeden"
                    className="form-control"
                    rows="3"
                    value={formData.kokNeden || ""}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <h5 className="mb-3">Düzeltme Faaliyeti</h5>

              <div className="row mb-3">
                <div className="col-md-12 mb-3">
                  <label className="form-label">Faaliyet Tanımı</label>
                  <textarea
                    name="duzetmeFaliyetTanimi"
                    className="form-control"
                    rows="2"
                    value={formData.duzetmeFaliyetTanimi || ""}
                    onChange={handleChange}
                  />
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">Sorumlu</label>
                  <DofPersonelSelect
                    personelList={personelList}
                    value={formData.duzeltmeSorumlu || ""}
                    onChange={(value) => setFormData((prev) => ({ ...prev, duzeltmeSorumlu: value }))}
                    loading={personelLoading}
                    disabled={saving}
                  />
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">Termin Tarihi</label>
                  <input
                    type="date"
                    name="duzeltmeTerminSure"
                    className="form-control"
                    value={toDateInputValue(formData.duzeltmeTerminSure)}
                    onChange={handleChange}
                  />
                </div>
              </div>
              {(!formData.faliyetTuru || formData.faliyetTuru === "duzeltici") && (
                <>
              <h5 className="mb-3">Düzeltici Faaliyet</h5>

              <div className="row mb-3">
                <div className="col-md-12 mb-3">
                  <label className="form-label">Faaliyet Tanımı</label>
                  <textarea
                    name="duzelticiFaliyetTanimi"
                    className="form-control"
                    rows="2"
                    value={formData.duzelticiFaliyetTanimi || ""}
                    onChange={handleChange}
                  />
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">Sorumlu</label>
                  <DofPersonelSelect
                    personelList={personelList}
                    value={formData.duzelticiSorumlu || ""}
                    onChange={(value) => setFormData((prev) => ({ ...prev, duzelticiSorumlu: value }))}
                    loading={personelLoading}
                    disabled={saving}
                  />
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">Termin Tarihi</label>
                  <input
                    type="date"
                    name="duzelticiTerminSure"
                    className="form-control"
                    value={toDateInputValue(formData.duzelticiTerminSure)}
                    onChange={handleChange}
                  />
                </div>
              </div>
                </>
              )}

              {(!formData.faliyetTuru || formData.faliyetTuru === "onleyici") && (
                <>
              <h5 className="mb-3">Önleyici Faaliyet</h5>

              <div className="row mb-3">
                <div className="col-md-12 mb-3">
                  <label className="form-label">Faaliyet Tanımı</label>
                  <textarea
                    name="onleyiciciFaliyetTanimi"
                    className="form-control"
                    rows="2"
                    value={formData.onleyiciciFaliyetTanimi || ""}
                    onChange={handleChange}
                  />
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">Sorumlu</label>
                  <DofPersonelSelect
                    personelList={personelList}
                    value={formData.onleyiciciSorumlu || ""}
                    onChange={(value) => setFormData((prev) => ({ ...prev, onleyiciciSorumlu: value }))}
                    loading={personelLoading}
                    disabled={saving}
                  />
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">Termin Tarihi</label>
                  <input
                    type="date"
                    name="onleyiciciTerminSure"
                    className="form-control"
                    value={toDateInputValue(formData.onleyiciciTerminSure)}
                    onChange={handleChange}
                  />
                </div>
              </div>
                </>
              )}

              <div className="d-flex justify-content-between">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={prevStep}
                  disabled={saving}
                >
                  Geri
                </button>

                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={nextStep}
                  disabled={saving}
                >
                  İleri
                </button>
              </div>
            </div>
          )}

          {currentStep === 4 && (
            <div>
              <h5 className="mb-3">Takip ve Karar</h5>

              <div className="row mb-3">
                <div className="col-md-12 mb-3">
                  <label className="form-label">Takip Tarihi</label>
                  <input
                    type="date"
                    name="takipTarihi"
                    className="form-control"
                    value={toDateInputValue(formData.takipTarihi)}
                    onChange={handleChange}
                  />
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">Takibi Gerçekleştiren</label>
                  <input
                    type="text"
                    name="takibiGerceklestiren"
                    className="form-control"
                    value={formData.takibiGerceklestiren || ""}
                    onChange={handleChange}
                  />
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">Takip Sonucu & Öneri</label>
                  <input
                    type="text"
                    name="takipSonucuOneri"
                    className="form-control"
                    value={formData.takipSonucuOneri || ""}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <h5 className="mb-3">
                DÖF Sonucu Değiştirilecek Doküman Var mı?
              </h5>

              <div className="row mb-3">
                <div className="col-md-4">
                  <label className="form-label">Prosedür</label>
                  <select
                    name="prosedur"
                    className="form-control"
                    value={boolVal(formData.prosedur)}
                    onChange={handleChange}
                  >
                    <option value="">Seçim Yapınız</option>
                    <option value="true">Evet</option>
                    <option value="false">Hayır</option>
                  </select>
                </div>

                <div className="col-md-4">
                  <label className="form-label">Rev. Tarihi</label>
                  <input
                    type="date"
                    name="prosedurRevTarihi"
                    className="form-control"
                    value={toDateInputValue(formData.prosedurRevTarihi)}
                    onChange={handleChange}
                  />
                </div>

                <div className="col-md-4">
                  <label className="form-label">Rev. No</label>
                  <input
                    type="text"
                    name="prosedurRevNo"
                    className="form-control"
                    value={formData.prosedurRevNo || ""}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-4">
                  <label className="form-label">Talimat</label>
                  <select
                    name="talimat"
                    className="form-control"
                    value={boolVal(formData.talimat)}
                    onChange={handleChange}
                  >
                    <option value="">Seçim Yapınız</option>
                    <option value="true">Evet</option>
                    <option value="false">Hayır</option>
                  </select>
                </div>

                <div className="col-md-4">
                  <label className="form-label">Rev. Tarihi</label>
                  <input
                    type="date"
                    name="talimatRevTarihi"
                    className="form-control"
                    value={toDateInputValue(formData.talimatRevTarihi)}
                    onChange={handleChange}
                  />
                </div>

                <div className="col-md-4">
                  <label className="form-label">Rev. No</label>
                  <input
                    type="text"
                    name="talimatRevNo"
                    className="form-control"
                    value={formData.talimatRevNo || ""}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-4 mb-3">
                  <label className="form-label">İş Akışı Değişecek mi?</label>
                  <select
                    name="isAkisi"
                    className="form-control"
                    value={boolVal(formData.isAkisi)}
                    onChange={handleChange}
                  >
                    <option value="">Seçim Yapınız</option>
                    <option value="true">Evet</option>
                    <option value="false">Hayır</option>
                  </select>
                </div>
                <div className="col-md-4 mb-3">
                  <label className="form-label">Rev. Tarihi</label>
                  <input
                    type="date"
                    name="isAkisiRevTarihi"
                    className="form-control"
                    value={toDateInputValue(formData.isAkisiRevTarihi)}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-4 mb-3">
                  <label className="form-label">Rev. No</label>
                  <input
                    type="text"
                    name="isAkisiRevNo"
                    className="form-control"
                    value={formData.isAkisiRevNo || ""}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-4 mb-3">
                  <label className="form-label">Form Değişecek mi?</label>
                  <select
                    name="form"
                    className="form-control"
                    value={boolVal(formData.form)}
                    onChange={handleChange}
                  >
                    <option value="">Seçim Yapınız</option>
                    <option value="true">Evet</option>
                    <option value="false">Hayır</option>
                  </select>
                </div>
                <div className="col-md-4 mb-3">
                  <label className="form-label">Rev. Tarihi</label>
                  <input
                    type="date"
                    name="formRevTarihi"
                    className="form-control"
                    value={toDateInputValue(formData.formRevTarihi)}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-4 mb-3">
                  <label className="form-label">Rev. No</label>
                  <input
                    type="text"
                    name="formRevNo"
                    className="form-control"
                    value={formData.formRevNo || ""}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-4 mb-3">
                  <label className="form-label">Diğer (Açıklama)</label>
                  <input
                    type="text"
                    name="digerRevAciklama"
                    className="form-control"
                    value={formData.digerRevAciklama || ""}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-4 mb-3">
                  <label className="form-label">Rev. Tarihi</label>
                  <input
                    type="date"
                    name="digerRevTarihi"
                    className="form-control"
                    value={toDateInputValue(formData.digerRevTarihi)}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-4 mb-3">
                  <label className="form-label">Rev. No</label>
                  <input
                    type="text"
                    name="digerRevNo"
                    className="form-control"
                    value={formData.digerRevNo || ""}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-4 mb-3">
                  <label className="form-label">Yeni DÖF Açılmalı Mı?</label>
                  <select
                    name="yeniDofAcilmali"
                    className="form-control"
                    value={boolVal(formData.yeniDofAcilmali)}
                    onChange={handleChange}
                  >
                    <option value="">Seçim Yapınız</option>
                    <option value="true">Evet</option>
                    <option value="false">Hayır</option>
                  </select>
                </div>
              </div>

              <h5 className="mb-3 border-top pt-3">
                DÖF Kapatma Tarihi ve Onayı
              </h5>

              <div className="row mb-3">
                <div className="col-md-4 mb-3">
                  <label className="form-label">DÖF Kapatma Tarihi</label>
                  <input
                    type="date"
                    name="dofKapatmaTarihiOnay"
                    className="form-control"
                    value={toDateInputValue(formData.dofKapatmaTarihiOnay)}
                    onChange={handleChange}
                  />
                </div>

                <div className="col-md-4 mb-3">
                  <label className="form-label">Yönetim Temsilcisi</label>
                  <input
                    type="text"
                    name="yonetimTemsilcisi"
                    className="form-control"
                    value={formData.yonetimTemsilcisi || ""}
                    onChange={handleChange}
                  />
                </div>

                <div className="col-md-4 mb-3">
                  <label className="form-label">DÖF İstenen Birim Amiri</label>
                  <input
                    type="text"
                    name="dofIstenenBirim"
                    className="form-control"
                    value={formData.dofIstenenBirim || ""}
                    onChange={handleChange}
                  />
                </div>
              </div>

              {fullAccess && (
                <div className="mb-3 border-top pt-3">
                  <h5 className="mb-3">DÖF Durumu (Tam Yetki)</h5>
                  <div className="row">
                    <div className="col-md-4 mb-3">
                      <label className="form-label">Durum</label>
                      <select className="form-control" value={manualStatus} onChange={(e) => setManualStatus(e.target.value)}>
                        {STATUS_OPTIONS.map(([value, label]) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        ))}
                      </select>
                      <span className="dof-hint">
                        Kapalı seçilip kapatma tarihi boş bırakılırsa bugünün tarihi yazılır. Kapalı dışında bir durum seçilirse kapatma tarihi silinir.
                      </span>
                    </div>
                    <div className="col-md-8 mb-3">
                      <label className="form-label d-block">Seçenekler</label>
                      <label className="d-flex align-items-center" style={{ gap: 8 }}>
                        <input
                          type="checkbox"
                          checked={formData.gecmisKayit === true}
                          onChange={(e) => {
                            const on = e.target.checked;
                            setFormData((prev) => ({ ...prev, gecmisKayit: on }));
                            if (on) setSendMail(false);
                          }}
                        />
                        Geçmiş (eski) DÖF kaydı — bu kayıt için hiç bildirim / hatırlatma maili gönderilmez
                      </label>
                      <label className="d-flex align-items-center" style={{ gap: 8 }}>
                        <input
                          type="checkbox"
                          checked={sendMail && formData.gecmisKayit !== true}
                          disabled={formData.gecmisKayit === true}
                          onChange={(e) => setSendMail(e.target.checked)}
                        />
                        Yeni atanan kişilere bildirim maili gönder
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {formData.assignedUsers && formData.assignedUsers.length > 0 && (
                <div className="mb-3 border-top pt-3">
                  <h5 className="mb-3">Atanmış Kişiler Özeti</h5>

                  <div className="table-responsive">
                    <table className="table table-sm table-bordered align-middle">
                      <thead>
                        <tr>
                          <th>Ad Soyad</th>
                          <th>E-posta</th>
                          <th>Departman</th>
                          <th>Görev</th>
                          <th>Atama Tipi</th>
                        </tr>
                      </thead>

                      <tbody>
                        {formData.assignedUsers.map((item, index) => (
                          <tr key={`summary-${item.userId}-${index}`}>
                            <td>{item.fullName || "-"}</td>
                            <td>{item.email || "-"}</td>
                            <td>{item.departman || "-"}</td>
                            <td>{item.gorev || "-"}</td>
                            <td>{ASSIGNMENT_LABELS[item.assignmentType] || item.assignmentType || "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div className="d-flex justify-content-between">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={prevStep}
                  disabled={saving}
                >
                  Geri
                </button>

                <button
                  type="submit"
                  className="btn btn-success"
                  disabled={saving}
                >
                  {readOnly ? "Salt Okunur" : saving ? "Kaydediliyor..." : "Güncelle"}
                </button>
              </div>
            </div>
          )}
          </fieldset>
        </form>
          </div>
        </section>
      </div>
    </Layout>
  );
}

export default DofEdit;
